import {
  Box,
  Divider,
  Flex,
  Heading,
  Icon,
  Link,
  Skeleton,
  SimpleGrid,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
  useColorModeValue,
} from '@chakra-ui/react';
import type { SimpleGridProps } from '@chakra-ui/react';
import { CalendarIcon, PhoneIcon } from '@chakra-ui/icons';
import { FaWallet } from 'react-icons/fa6';

import SectionPanel from '../../components/SectionPanel';
import { NOT_PROVIDED } from '../../constants';
import { formatAddress, getFullName, getSecondaryPhone } from '../../helpers';
import getDateFromString from 'helpers/getDateFromString';
import { formatCurrency } from 'views/quote_marketplace/constants';

import {
  useClientCenterCustomerProfile,
  useClientCenterCustomerRelatedContacts,
  useClientCenterCustomerAccountSummary,
} from '../../hooks';

import ClientAccountBadge from './ClientAccountBadge';
import RelatedContactsBadge from './RelatedContactsBadge';

interface CustomerProfileProps {
  customerId: string;
}

interface ProfileField {
  key: string;
  label: string;
  loading?: boolean;
  render: () => JSX.Element;
}

const formatDateOfBirth = (dateOfBirth: string | null): string | null => {
  if (!dateOfBirth) {
    return null;
  }

  const [year, month, day] = dateOfBirth.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getClientSinceYear = (
  contactStatusDate: string | null,
): string | null => {
  const date = contactStatusDate ? getDateFromString(contactStatusDate) : null;
  return date ? String(date.getFullYear()) : null;
};

const CustomerProfile = ({ customerId }: CustomerProfileProps) => {
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.900', 'white');
  const balanceBg = useColorModeValue('orange.50', 'whiteAlpha.100');
  const balanceBorder = useColorModeValue('orange.200', 'orange.300');
  const balanceColor = useColorModeValue('orange.600', 'orange.300');

  const { profile, policyTypes, isLoading, isPolicyTypesLoading } =
    useClientCenterCustomerProfile(customerId);
  const {
    relatedContacts,
    isLoading: isRelatedContactsLoading,
    error: relatedContactsError,
  } = useClientCenterCustomerRelatedContacts(customerId);
  const { accountSummary } = useClientCenterCustomerAccountSummary(customerId);

  const renderText = (value: string | null) => () => (
    <Text color={textColor}>{value || NOT_PROVIDED}</Text>
  );

  const renderPhone = (value: string | null) => () =>
    value ? (
      <Tooltip label="Call with RingCentral" placement="top" hasArrow>
        <Link
          href={`rcapp://r/dialer?number=${value}`}
          color={textColor}
          display="inline-flex"
          alignItems="center"
          gap="1"
        >
          <PhoneIcon boxSize="3" />
          {value}
        </Link>
      </Tooltip>
    ) : (
      <Text color={textColor}>{NOT_PROVIDED}</Text>
    );

  const clientSinceYear = getClientSinceYear(
    profile?.contact_status_date ?? null,
  );
  const showPolicyTypes = !isPolicyTypesLoading && policyTypes.length > 0;

  const fieldRows: {
    fields: ProfileField[];
    columns: SimpleGridProps['columns'];
  }[] = [
    {
      fields: [
        {
          key: 'address',
          label: 'Address',
          render: renderText(formatAddress(profile)),
        },
        {
          key: 'date_of_birth',
          label: 'Date of Birth',
          render: renderText(formatDateOfBirth(profile?.date_of_birth ?? null)),
        },
        {
          key: 'primary_phone',
          label: 'Primary Phone',
          render: renderPhone(profile?.home_phone || null),
        },
        {
          key: 'secondary_phone',
          label: 'Secondary Phone',
          render: renderPhone((profile && getSecondaryPhone(profile)) || null),
        },
        {
          key: 'email',
          label: 'Email',
          render: renderText(profile?.email || null),
        },
      ],
      columns: { base: 1, sm: 2, lg: 5 },
    },
    {
      fields: [
        {
          key: 'business_name',
          label: 'Business Name',
          render: renderText(profile?.business_name || null),
        },
        {
          key: 'business_number',
          label: 'Business Number',
          render: renderText(profile?.business_number || null),
        },
        {
          key: 'approved_contacts',
          label: 'Approved Contacts',
          render: renderText(profile?.approved_contacts || null),
        },
      ],
      columns: { base: 1, sm: 2, lg: 3 },
    },
    {
      fields: [
        {
          key: 'client_code',
          label: 'Client Code',
          render: renderText(profile?.client_code || null),
        },
        {
          key: 'branch_code',
          label: 'Branch Code',
          render: renderText(profile?.branch_code || null),
        },
        {
          key: 'producer',
          label: 'Producer',
          render: renderText(profile?.executive_name || null),
        },
        {
          key: 'account_manager',
          label: 'Account Manager',
          render: renderText(profile?.handler_name || null),
        },
      ],
      columns: { base: 1, sm: 2, lg: 4 },
    },
  ];

  const headerContent = (
    <Flex align="flex-start" gap="6" wrap="wrap">
      <Box>
        <Flex align="center" gap="2" wrap="wrap">
          {isLoading ? (
            <Skeleton height="24px" width="180px" borderRadius="sm" />
          ) : (
            <Heading size="md" color={textColor} fontWeight="700">
              {(profile && getFullName(profile)) || NOT_PROVIDED}
            </Heading>
          )}
          {!isRelatedContactsLoading && (
            <RelatedContactsBadge
              customerId={customerId}
              relatedContacts={relatedContacts}
              hasError={Boolean(relatedContactsError)}
            />
          )}
        </Flex>
        {profile?.office_name && (
          <Text color={labelColor} fontSize="sm" mt="1">
            {profile.office_name}
          </Text>
        )}
        {(showPolicyTypes || clientSinceYear) && (
          <Wrap spacing="1" mt="1" align="center">
            {showPolicyTypes &&
              policyTypes.map((policyType) => (
                <WrapItem key={policyType}>
                  <Tag size="sm" variant="subtle" borderRadius="full">
                    <TagLabel>{policyType}</TagLabel>
                  </Tag>
                </WrapItem>
              ))}
            {clientSinceYear && (
              <WrapItem>
                <Tag size="sm" variant="subtle" borderRadius="full">
                  <TagLeftIcon as={CalendarIcon} boxSize="2.5" />
                  <TagLabel>Client since {clientSinceYear}</TagLabel>
                </Tag>
              </WrapItem>
            )}
          </Wrap>
        )}
      </Box>
      {accountSummary?.account_balance != null && (
        <Flex direction="column" gap="2">
          <Flex
            align="center"
            justify="space-between"
            gap="6"
            px="4"
            py="3"
            borderRadius="lg"
            borderWidth="1px"
            borderColor={balanceBorder}
            bg={balanceBg}
          >
            <Box>
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="wide"
                textTransform="uppercase"
                color={balanceColor}
              >
                Client Current Value
              </Text>
              <Text
                fontSize="2xl"
                fontWeight="800"
                lineHeight="short"
                color={balanceColor}
              >
                {formatCurrency(accountSummary.account_balance)}
              </Text>
            </Box>
            <Icon as={FaWallet} boxSize="5" color={balanceColor} />
          </Flex>
          <ClientAccountBadge accountSummary={accountSummary} />
        </Flex>
      )}
    </Flex>
  );

  return (
    <SectionPanel headerContent={headerContent} accent>
      <Box data-customer-id={customerId}>
        <Divider mb="4" />
        <Heading size="sm" color={textColor} fontWeight="700" mb="4">
          Client Information
        </Heading>
        {fieldRows.map(({ fields, columns }, index) => (
          <Box key={fields[0].key} mt={index > 0 ? '4' : undefined}>
            {index > 0 && <Divider mb="4" />}
            <SimpleGrid columns={columns} spacing="4">
              {fields.map(({ key, label, loading = isLoading, render }) => (
                <Box key={key}>
                  <Text fontSize="xs" color={labelColor} mb="1">
                    {label}
                  </Text>
                  {loading ? (
                    <Skeleton height="16px" width="140px" borderRadius="sm" />
                  ) : (
                    render()
                  )}
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        ))}
        <Divider my="4" />
        <Heading size="sm" color={textColor} fontWeight="700" mb="4">
          Additional Information
        </Heading>
        {isLoading ? (
          <Skeleton height="16px" width="140px" borderRadius="sm" />
        ) : (
          <Text color={textColor} whiteSpace="pre-wrap" wordBreak="break-word">
            {profile?.comment || NOT_PROVIDED}
          </Text>
        )}
      </Box>
    </SectionPanel>
  );
};

export default CustomerProfile;
